import { gql, useQuery } from '@apollo/client';

import { useWebflowPoc } from '@/hooks/useWebflowPoc';
import { usesWebflowNavChrome } from '@/lib/webflow-poc/navigation-chrome';

export type WebflowNavChromeData = {
  available: boolean;
  html: string | null;
  inlineStyles: string[];
  wrapperClassName: string;
  /** jQuery / webflow.js URLs for dynamic-page w-nav re-init. */
  scriptSrcs: string[];
};

type WebflowNavChromeQuery = {
  webflowIntegrationGetNavChrome: {
    result: WebflowNavChromeData;
  };
};

export const WEBFLOW_NAV_CHROME_QUERY = gql`
  query WebflowIntegrationGetNavChrome {
    webflowIntegrationGetNavChrome {
      result {
        available
        html
        inlineStyles
        wrapperClassName
        scriptSrcs
      }
    }
  }
`;

/**
 * Loads the Webflow navbar fragment for dynamic Tradewing pages.
 * Requires featureFlags.webflow + connected integration + WEBFLOW/WEBFLOW_NATIVE.
 * Otherwise skips the query and leaves the Tradewing header untouched.
 */
export const useWebflowNavChrome = () => {
  const { isEnabled, navigationChrome } = useWebflowPoc();
  const configuredForWebflowNav =
    isEnabled && usesWebflowNavChrome(navigationChrome);

  const { data, loading, error } = useQuery<WebflowNavChromeQuery>(
    WEBFLOW_NAV_CHROME_QUERY,
    {
      skip: !configuredForWebflowNav,
      fetchPolicy: 'cache-first',
    }
  );

  const result = data?.webflowIntegrationGetNavChrome?.result;
  const available =
    configuredForWebflowNav &&
    result?.available === true &&
    typeof result.html === 'string' &&
    result.html.length > 0;

  const nav: WebflowNavChromeData | null = available && result
    ? {
        ...result,
        scriptSrcs: Array.isArray(result.scriptSrcs) ? result.scriptSrcs : [],
      }
    : null;

  return {
    /** Tenant is set to show Webflow nav on dynamic pages (may still be loading). */
    configuredForWebflowNav,
    navigationChrome,
    /** Marker found and HTML ready to render. */
    available,
    loading: configuredForWebflowNav && loading,
    error,
    nav,
  };
};
